import type { CalendarDay } from '../types/domain';
import type { Track } from '../types/domain';
import type {
  ClassScheduleSettings,
  PageSectionLayout,
  ProgramColorSettings,
} from '../services/sectionLayoutService';
import type { ClassMemberRow } from './classMembersStorage';

export interface PresetMenuGradeStatus {
  academicCalendar: boolean;
  classSchedule: boolean;
  gradeTrack: boolean;
  classMembers: boolean;
  timetableSettings: boolean;
}

function gradeTrackSectionHasData(section: PageSectionLayout | undefined): boolean {
  return (section?.trackColumns ?? []).some((column) => (column.name ?? '').trim().length > 0);
}

function academicSectionHasPresetContent(
  section: PageSectionLayout,
  baseCalendarDays: CalendarDay[]
): boolean {
  const days = section.academicCalendarDays ?? [];
  if (days.length > 0) return true;
  if (section.id === 'base' && baseCalendarDays.length > 0) return true;
  const settings = section.academicCalendarSettings;
  const winterFrom = (settings?.winterBreakStartsOn ?? '').trim();
  const winterTo = (settings?.winterBreakEndsOn ?? '').trim();
  return Boolean(winterFrom && winterTo);
}

/** 학사일정: 클래스 등록 + 실제 학사 본문(휴일·토일수업일·겨울방학 등). 초기화만 한 기본 설정은 미준비 */
export function isGradeAcademicCalendarPresetReady(
  workspaceId: string,
  gradeId: string,
  sections: PageSectionLayout[],
  baseCalendarDays: CalendarDay[] = []
): boolean {
  const gid = gradeId.trim();
  if (!gid || !workspaceId) return false;
  return sections.some(
    (section) =>
      section.gradeIds.includes(gid) && academicSectionHasPresetContent(section, baseCalendarDays)
  );
}

/** 수업시간: 섹션에 클래스가 등록되어 있으면 준비됨 (미등록 시 base 데이터로 초록 처리하지 않음) */
export function isGradeClassSchedulePresetReady(
  gradeId: string,
  settings: ClassScheduleSettings
): boolean {
  const gid = gradeId.trim();
  if (!gid) return false;
  return settings.sections.some((section) => section.gradeIds.includes(gid));
}

/** 편제 및 기준 시수: 편제·컬럼 데이터가 있을 때만 준비됨 (클래스만 연결된 빈 섹션은 미준비) */
export function isGradeGradeTrackPresetReady(
  gradeId: string,
  sections: PageSectionLayout[],
  tracks: Track[]
): boolean {
  const gid = gradeId.trim();
  if (!gid) return false;

  if (tracks.some((track) => (track.grade_id ?? '').trim() === gid)) return true;

  const gradeSection = sections.find((section) => section.id === `grade:${gid}`);
  if (gradeTrackSectionHasData(gradeSection)) return true;

  const linkedSection = sections.find((section) => section.gradeIds.includes(gid));
  return gradeTrackSectionHasData(linkedSection);
}

/** 클래스 멤버: 저장된 멤버가 1명 이상이면 준비됨(이름 미입력 포함) */
export function isGradeClassMembersPresetReady(members: ClassMemberRow[]): boolean {
  return members.length > 0;
}

/** 시간표 설정: 클래스별 기초시간표 또는 프로그램 색상이 저장되어 있으면 준비됨 */
export function isGradeTimetableSettingsPresetReady(
  gradeId: string,
  settings: ProgramColorSettings | null | undefined
): boolean {
  const gid = gradeId.trim();
  if (!gid || !settings) return false;
  return (
    Object.prototype.hasOwnProperty.call(settings.baseTimetableByGrade, gid) ||
    Object.prototype.hasOwnProperty.call(settings.colorsByGrade, gid)
  );
}

export function computePresetMenuGradeStatus(params: {
  workspaceId: string;
  gradeId: string;
  academicCalendarSections: PageSectionLayout[];
  classScheduleSettings: ClassScheduleSettings;
  gradeTrackSections: PageSectionLayout[];
  tracks: Track[];
  classMembers: ClassMemberRow[];
  programColorSettings?: ProgramColorSettings | null;
  baseCalendarDays?: CalendarDay[];
}): PresetMenuGradeStatus {
  const { workspaceId, gradeId } = params;
  return {
    academicCalendar: isGradeAcademicCalendarPresetReady(
      workspaceId,
      gradeId,
      params.academicCalendarSections,
      params.baseCalendarDays
    ),
    classSchedule: isGradeClassSchedulePresetReady(gradeId, params.classScheduleSettings),
    gradeTrack: isGradeGradeTrackPresetReady(gradeId, params.gradeTrackSections, params.tracks),
    classMembers: isGradeClassMembersPresetReady(params.classMembers),
    timetableSettings: isGradeTimetableSettingsPresetReady(gradeId, params.programColorSettings),
  };
}
